import { UserState } from './../../../models/_core/user-state';
import { HelperUtilitiesService } from 'src/app/services/_core/helper-utilities/helper-utilities.service';
import { AuthState } from 'src/app/models/_core/auth-state';
import { VerlockerService } from '../verlocker/verlocker.service';
import { NavController, Platform } from '@ionic/angular';
import { User } from 'src/app/models/user';
import { StorageService } from '../storage/storage.service';
import { environment } from 'src/environments/environment';
import { AuthService } from 'src/app/services/_core/auth/auth.service';
import { Injectable } from '@angular/core';
import { CanActivate, ActivatedRouteSnapshot, Router, UrlTree } from '@angular/router';
import { AnalyticsService } from '../analytics/analytics.service';
import { first } from 'rxjs/operators';
import moment from 'moment';
import { NavigationService } from '../../navigation/navigation.service';
import { firstValueFrom } from 'rxjs';
import { NotificationsService } from '../notifications/notifications.service';
import { ErrorHandlerService } from '../error-handler/error-handler.service';
import { UserDeviceService } from '../user-device/user-device.service';

/**
 * ID: bh-login-route-guard-service
 * Name: BH Login Route Guard Service
 * Description: Service used to protect user-restricted pages and routes
 * Version: 2
 *
 * ==============================
 * Change Log
 * ==============================
 * 2021-07-02 - MW - v1: Initial dev
 * 2021-07-13 - MW - v2: Implemented userState
 */
@Injectable({
  providedIn: 'root'
})
export class LoginRouteGuardService implements CanActivate {
  env = environment;
  private authState: AuthState;
  private authUser: User;

  constructor(
    private authService: AuthService,
    private storageService: StorageService,
    private analytics: AnalyticsService,
    private navService: NavigationService,
    private verlocker: VerlockerService,
    private notifications: NotificationsService,
    private helpers: HelperUtilitiesService,
    private errorHandler: ErrorHandlerService,
    private deviceService: UserDeviceService,
    private platform: Platform
  ) {
  }

  /***
   * Checks if user is logged in and if not, reroutes to login
   * @param route Activated Route Snapshot
   */
  async canActivate(route: ActivatedRouteSnapshot): Promise<boolean | UrlTree> {
    this.authUser = this.authService.getAuthUser();
    const validSession = await this.validateSession();
    const hasAccess = await this.validateAccess(route);
    const allowNavigate = (this.authUser && this.authUser.userId !== null && validSession && hasAccess);
    if (!allowNavigate) {
      this.saveTargetUrl(route);
      // console.log('loginRouteGuard: navigating to login', this.authService.targetUrl);
      return this.navService.navigateRoot('/login');
    }
    return allowNavigate;
  }

  async validateSession(): Promise<boolean> {
    let isValidSession = false;
    // Check app version
    this.verlocker.setCheckVersion(true);
    // Check token and existing user
    if (!this.authUser || !this.authUser.userId) {
      // Check if token storing is allowed
      if (this.env.storeToken) {
        try {
          let userState: UserState = await this.storageService.getData('userState');
          let userData = userState?.authUser;
          console.log('validateSession: userState, userData', userState, userData);

          // If no userState in storage, try to get user info from /me endpoint
          if (!userState || !userData) {
            console.log('validateSession: No userState in storage, attempting to fetch from /me endpoint');
            console.log('validateSession: Current authUser:', this.authUser);
            console.log('validateSession: Current authUser token:', this.authUser?.token);

            // Only try /me endpoint if we have a token
            if (this.authUser?.token) {
              try {
                const meResponse = await firstValueFrom(this.authService.validateUser());
                console.log('validateSession: /me endpoint response:', meResponse);
                if (meResponse && meResponse.id) {
                  // Reconstruct userState from /me response
                  userState = await this.authService.reconstructUserStateFromMe(meResponse, this.authUser.token);
                  userData = userState.authUser;

                  // Save the reconstructed userState
                  this.authService.userState = userState;
                  await this.authService.saveUserStateToStorage();

                  console.log('validateSession: Reconstructed userState from /me endpoint', userState);
                  console.log('validateSession: Reconstructed userData from /me endpoint', userData);
                } else {
                  console.log('validateSession: /me endpoint response invalid or missing id:', meResponse);
                }
              } catch (meError) {
                console.error('validateSession: Failed to fetch user from /me endpoint', meError);
                console.error('validateSession: /me endpoint error details:', meError);
              }
            } else {
              console.log('validateSession: No token available, cannot call /me endpoint');
            }
          }

          // Check for outdated session
          console.log('validateSession: Checking session validity:');
          console.log('validateSession: userState exists:', !!userState);
          console.log('validateSession: userState.authState:', userState?.authState);
          console.log('validateSession: AuthState.LOGGED_IN:', AuthState.LOGGED_IN);
          console.log('validateSession: authState matches:', userState?.authState === AuthState.LOGGED_IN);
          console.log('validateSession: sessionAppVersion exists:', !!userState?.sessionAppVersion);
          console.log('validateSession: sessionAppVersion:', userState?.sessionAppVersion);
          console.log('validateSession: expected version:', this.env.appVersion + '-' + this.env.env);
          console.log('validateSession: version matches:', userState?.sessionAppVersion === (this.env.appVersion + '-' + this.env.env));

          if (
            userState &&
            userState.authState === AuthState.LOGGED_IN &&
            userState.sessionAppVersion &&
            userState.sessionAppVersion === this.env.appVersion + '-' + this.env.env
          ) {

            console.log('loginRouteGuard: validateSession: userState', userState);
            console.log('loginRouteGuard: validateSession: userData', userData);
            // Check for userData and token
            if (
              userData &&
              userData.token
            ) {
              this.authUser.token = userData.token;
              this.authService.setAuthUser(this.authUser);
              this.authService.userState = userState;
              this.authService.userState.sessionRefreshed = moment().format('M/D/YYYY HH:mm');
              try {
                // Check if user is still active in user table
                // const userRes = await firstValueFrom(this.usersService.getById(userData.userId));
                // Check if user is still AD valid. if not go to login
                const userRes = userData;
                if (userRes) {
                  this.authUser = userRes;
                  try {
                    const res = await firstValueFrom(this.authService.validateUser());
                    console.log('loginRouteGuard: validateUser: res', res.body);
                    const validUser = res && res.body.name ? this.authUser : null;
                    console.log('loginRouteGuard: validateUser: validUser', validUser);
                    if (!validUser || !validUser.userId) {
                      this.analytics.customEvent('auto-login', 'Invalid login', '');
                      // console.log('loginRouteGuard: user invalid');
                      return Promise.resolve(false);
                    } else {
                      this.analytics.analyticsData.userid = validUser.name.toLowerCase();
                      // Setting authenticated to false because user used stored token
                      this.authUser.authenticated = false;
                      this.authService.setAuthUser(this.authUser);
                      this.analytics.customEvent('auto-login', 'Valid login', validUser.name);
                      isValidSession = true;
                    }
                  } catch (err) {
                    console.error('loginRouteGuard: Validate User Error', err);
                  }
                } else {
                  console.error('loginRouteGuard: No user record found');
                }
              } catch (err) {
                console.error('loginRouteGuard: User Service Error', err);
              }
            } else {
              // Check if has user/token, if not go to login
              console.error('loginRouteGuard: No token, go to login');
            }
          } else {
            console.error('loginRouteGuard: Outdated session, go to login');
            console.error('loginRouteGuard: Session validation failed - userState:', !!userState);
            console.error('loginRouteGuard: Session validation failed - authState match:', userState?.authState === AuthState.LOGGED_IN);
            console.error('loginRouteGuard: Session validation failed - sessionAppVersion exists:', !!userState?.sessionAppVersion);
            console.error('loginRouteGuard: Session validation failed - version match:', userState?.sessionAppVersion === (this.env.appVersion + '-' + this.env.env));
          }
        } catch (err) {
          // Check if has user/token, if not go to login
          console.error('loginRouteGuard: userData error', err);
        }
      } else {
        console.error('loginRouteGuard: No stored tokens allowed, login');
        // return Promise.resolve(true);
        // return Promise.resolve(false);
      }
    } else {
      // if (!this.profile || this.profile.profileStatus !== 'Loaded') {
      //   this.authUser.authenticated = false;
      // }
      // Do nothing; all set
      // console.log('loginRouteGuard: OK to go to page');
      isValidSession = true;
    }
    return Promise.resolve(isValidSession);
  }

  async validateAccess(route: ActivatedRouteSnapshot): Promise<boolean> {
    const authUser = this.authService.getAuthUser();
    const roles = route.data['roles'];
    let hasAccess = false;
    if (roles && roles.length > 0) {
      roles.forEach(role => {
        if (authUser.role === role) {
          hasAccess = true;
        }
      });
    } else {
      hasAccess = true;
    }
    return Promise.resolve(hasAccess);
  }

  /**
   * Saves the URL we're trying to access so we can redirect after login.
   * Uses the route we're guarding (attempted path), not window.location, so we
   * never save /login when the user is already on the login page.
   */
  async saveTargetUrl(route?: ActivatedRouteSnapshot) {
    try {
      let path = route ? this.getAttemptedPathFromRoute(route) : window.location.pathname;
      // On iOS/device, /tabs/surveys is blocked by DeviceRouteGuard; save survey-selection so post-login goes there directly
      if (this.isDeviceAndSurveysPath(path)) {
        path = '/tabs/survey-selection';
      }
      console.log('saveTargetUrl', path);
      if (
        path.indexOf('redirect') === -1 &&
        path.indexOf('login') === -1 &&
        path !== '' &&
        path !== '/'
      ) {
        const targetUrl = path + (window.location.search ? window.location.search : '');
        this.authService.targetUrl = targetUrl;
        await this.storageService.saveData('targetUrl', targetUrl, false);
        console.log('target url saved', targetUrl);
      }
    } catch (err) {
      this.errorHandler.handleError(err, 'saveTargetUrl');
    }
  }

  /** True when on iOS/device and path is /tabs/surveys or /surveys (blocked on device; user should land on survey-selection). */
  private isDeviceAndSurveysPath(path: string): boolean {
    const isSurveysPath = path === '/tabs/surveys' || path === '/surveys';
    if (!isSurveysPath) return false;
    // Use Platform first so it works before async Capacitor device load
    if (this.platform.is('ios') || this.platform.is('capacitor') || this.platform.is('cordova')) {
      return true;
    }
    const device = this.deviceService.getUserDevice();
    return !!(device?.os?.toLowerCase() === 'ios' || device?.isMobileDevice);
  }

  /** Build the attempted path from the route snapshot we're guarding. */
  private getAttemptedPathFromRoute(route: ActivatedRouteSnapshot): string {
    const parts = route.pathFromRoot
      .map((r) => (r.url && r.url.length ? r.url.map((s) => s.path).join('/') : ''))
      .filter(Boolean);
    return parts.length ? '/' + parts.join('/') : '/';
  }

}
